import { InfiniteTable, InfiniteTableColumns, InfiniteTableSortBy, Pill, Stack } from '@sixfold/common-ui';
import React, { useCallback, useMemo, useState } from 'react';
import { NavLink } from 'react-router';

import { useUrlSort } from '../../lib/util/url';
import { formatIntegrationType } from '../entities';

export interface Props {
  telematicsProviders: TableProviderProps[];
}

interface TableProviderProps {
  id: string;
  name: string;
  integrationType: string | null;
  isTourBasedProvider: boolean | null;
  isAddingIntegrationDisabled: boolean | null;
  isExternalConfigurationNeeded: boolean;
  isGlobalAllocationForbidden: boolean | null;
  integrationInstructionsURL: string | null;
  isVehicleManagementEnabled: boolean | null;
  isInternalUseOnly: boolean;
  isDeprecated: boolean | null;
  numIntegrations: number;
  numActiveIntegrations: number;
  numTotalTrucks: number;
  numActiveTrucks: number;
  recentAllocationsCount: number;
}

export type SortableColumn = (typeof sortableColumns)[number];
const sortableColumns = [
  'name',
  'numIntegrations',
  'numActiveIntegrations',
  'numTotalTrucks',
  'numActiveTrucks',
  'recentAllocationsCount',
] as const;

export const useProvidersListSort = () => useUrlSort<SortableColumn>({ key: 'name', direction: 'ASC' });

const countOrUnknown = (value: number): string => (value === -1 ? '—' : value.toString());

export const ProvidersList: React.FC<Props> = ({ telematicsProviders }) => {
  const { sort, changeSort } = useProvidersListSort();

  const sortBy = useMemo(
    (): InfiniteTableSortBy<keyof TableProviderProps> => ({
      columnId: sort.key,
      order: sort.direction,
    }),
    [sort],
  );

  const onSortByChange = useCallback(
    (newSort: InfiniteTableSortBy<keyof TableProviderProps>) => {
      if (sortableColumns.includes(newSort.columnId as SortableColumn)) {
        changeSort({ key: newSort.columnId as SortableColumn, direction: newSort.order });
      }
    },
    [changeSort],
  );

  const columns: InfiniteTableColumns<TableProviderProps> = useMemo(
    () => [
      {
        id: 'name',
        title: 'Name',
        width: '18%',
        isUserSortable: true,
        cellContent: ({ row }) => <NavLink to={`/telematics/providers/${row.id}`}>{row.name}</NavLink>,
      },
      {
        id: 'isAddingIntegrationDisabled',
        title: 'Flags',
        width: 'auto',
        cellContent: ({ row }) => (
          <Stack direction="row" gap="xsmall" wrap={true}>
            {row.isAddingIntegrationDisabled && <Pill kind="info">Adding integration disabled</Pill>}
            {row.isInternalUseOnly && <Pill kind="warning">Internal use only</Pill>}
            {row.isDeprecated && <Pill kind="danger">Deprecated</Pill>}
            {row.isExternalConfigurationNeeded && <Pill kind="info">External configuration needed</Pill>}
            {row.isGlobalAllocationForbidden && <Pill kind="warning">Global allocation forbidden</Pill>}
            {row.isVehicleManagementEnabled && <Pill kind="info">Vehicle management enabled</Pill>}
            {row.integrationType && <Pill kind="success">{formatIntegrationType(row.integrationType)}</Pill>}
            {row.isTourBasedProvider && <Pill kind="info">Tour based provider</Pill>}
          </Stack>
        ),
      },
      {
        id: 'numIntegrations',
        title: 'Integrations',
        width: '10%',
        isUserSortable: true,
        cellContent: ({ row }) => (
          <NavLink to={`/telematics/providers/${row.id}`}>{countOrUnknown(row.numIntegrations)}</NavLink>
        ),
      },
      {
        id: 'numActiveIntegrations',
        title: 'Active Integrations',
        width: '12%',
        isUserSortable: true,
        cellContent: ({ row }) => (
          <NavLink to={`/telematics/providers/${row.id}`}>{countOrUnknown(row.numActiveIntegrations)}</NavLink>
        ),
      },
      {
        id: 'numTotalTrucks',
        title: 'Total trucks',
        width: '10%',
        isUserSortable: true,
        cellContent: ({ row }) => <>{countOrUnknown(row.numTotalTrucks)}</>,
      },
      {
        id: 'numActiveTrucks',
        title: 'Active trucks',
        width: '10%',
        isUserSortable: true,
        cellContent: ({ row }) => <>{countOrUnknown(row.numActiveTrucks)}</>,
      },
      {
        id: 'recentAllocationsCount',
        title: 'Allocations',
        width: '10%',
        isUserSortable: true,
        cellContent: ({ row }) => <>{countOrUnknown(row.recentAllocationsCount)}</>,
      },
    ],
    [],
  );

  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  const containerRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node === null) return;
    let el: HTMLElement | null = node.parentElement;
    while (el !== null) {
      const { overflow, overflowY } = getComputedStyle(el);
      if (overflow === 'auto' || overflow === 'scroll' || overflowY === 'auto' || overflowY === 'scroll') {
        setScrollParent(el);
        return;
      }
      el = el.parentElement;
    }
  }, []);

  return (
    <div ref={containerRefCallback}>
      <InfiniteTable<TableProviderProps>
        columns={columns}
        data={telematicsProviders}
        sortBy={sortBy}
        onSortByChange={onSortByChange}
        scrollParent={scrollParent}
        className="compact-table"
      />
    </div>
  );
};
